import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ProgramBundleType } from "@/modules/programBundle/api/programBundleDto"
import { useProgramBundleRights } from "@/modules/programBundle/hooks/useProgramBundleRights"
import { ProgramBundleInfoDetails } from "@modules/programBundle/components/programBundleInfoDetails"
import { ProgramBundleInfoHeader } from "@modules/programBundle/components/programBundleInfoHeader"
import { ProgramBundleInfoPrograms } from "@modules/programBundle/components/programBundleInfoPrograms"
import { useProgramBundle } from "@modules/programBundle/hooks/useProgramBundle"
import { ProgramBundleInfoSubscribersList } from "@modules/programBundle/programBundleSubscribers/components/programBundleInfoSubscribersList"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { ErrorCard } from "@modules/ui/components/error/errorCard"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { startTransition, Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

type ProgramBundleInfoWrapperProps = {
  programBundleId: string
  className?: string
} & React.ComponentProps<"div">

export enum ProgramBundleDetailsTabs {
  DETAILS = "0",
  PROGRAMS = "1",
  MEMBERS = "2",
}

export function ProgramBundleInfoWrapper({ programBundleId, className }: ProgramBundleInfoWrapperProps) {
  const { data: programBundle } = useProgramBundle(programBundleId)
  const { isAuthor } = useProgramBundleRights(programBundleId)
  const { setCurrentStackState, getCurrentStackState } = useSidebar()

  const activeIndex = getCurrentStackState("activeIndex") || ProgramBundleDetailsTabs.DETAILS
  function selectTab(index: string) {
    startTransition(() => {
      setCurrentStackState("activeIndex", index.toString())
    })
  }

  return (
    <ErrorBoundary fallback={<ErrorCard />}>
      <Suspense fallback={<LoadingSpinner />}>
        <div className={cn("flex grow-1 flex-col", className)}>
          <ProgramBundleInfoHeader programBundleId={programBundleId} />
          <Tabs defaultValue={activeIndex} className="relative flex w-full flex-col">
            <TabsList variant="program">
              <TabsTrigger variant="program" onClick={() => selectTab("0")} value="0" className="flex-1 rounded-4xl">
                {word("programBundle.tabs.details")}
              </TabsTrigger>
              <TabsTrigger variant="program" onClick={() => selectTab("1")} value="1" className="flex-1 rounded-4xl">
                {word("programBundle.tabs.programs")}
              </TabsTrigger>
              {isAuthor && (
                <TabsTrigger variant="program" onClick={() => selectTab("2")} value="2" className="flex-1 rounded-4xl">
                  {programBundle.type === ProgramBundleType.ON_OFF
                    ? word("programBundle.tabs.athletes")
                    : word("programBundle.tabs.subscribers")}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="0" className="flex w-full">
              <Suspense fallback={<LoadingSpinner className="pt-18" />}>
                <ProgramBundleInfoDetails programBundle={programBundle} />
              </Suspense>
            </TabsContent>
            <TabsContent value="1" className="flex w-full">
              <Suspense fallback={<LoadingSpinner className="pt-18" />}>
                <ProgramBundleInfoPrograms programBundle={programBundle} />
              </Suspense>
            </TabsContent>
            <TabsContent value="2" className="flex w-full">
              <Suspense fallback={<LoadingSpinner className="pt-18" />}>
                <ProgramBundleInfoSubscribersList programBundle={programBundle} />
              </Suspense>
            </TabsContent>
          </Tabs>
        </div>
      </Suspense>
    </ErrorBoundary>
  )
}
